import React from "react";
import { Routes, Route, useLocation, Navigate } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";

/*
|---------------------------------------------------------------------
|                             Routes
|---------------------------------------------------------------------
*/

import { AuthContext, AuthProvider } from "./contexts/AuthContext";
import AuthLayout from "./layouts/AuthLayout";
import Signin from "./pages/auth/Signin";
import Verify from "./pages/auth/Verify";
import Requests from "./pages/requests/Requests";
import Request from "./pages/requests/Request";
import NewRequest from "./pages/requests/NewRequest";
import EditRequest from "./pages/requests/EditRequest";
import Change from "./pages/home/Change";
import Modules from "./pages/modules/Modules";
import Roles from "./pages/roles/Roles";
import AutoLayout from "./layouts/AutoLayout";
import ChanLayout from "./layouts/ChanLayout";
import Users from "./pages/users/Users";
import Companies from "./pages/companies/Companies";

import NewAutoRequest from "./pages/autoline/NewAutoRequest";
import OneAutoRequest from "./pages/autoline/OneAutoRequest";
import AllAutoRequests from "./pages/autoline/AllAutoRequests";
import Units from "./pages/units/Units";
import AutoDashboard from "./pages/autoline/AutoDashboard";

function App() {
  const queryClient = new QueryClient();
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <Routes>
          <Route element={<AuthLayout />}>
            <Route path="/signin" element={<Signin />} />
            <Route path="/verify" element={<Verify />} />
            <Route path="/" element={<Change />} />
          </Route>
          <Route element={<ChanLayout />}>
            <Route
              path="/change/requests"
              element={
                <ProtectedRoute>
                  <Requests />
                </ProtectedRoute>
              }
            />
            <Route
              path="/change/requests/:id/details"
              element={
                <ProtectedRoute>
                  <Request />
                </ProtectedRoute>
              }
            />
            <Route
              path="/change/requests/new"
              element={
                <ProtectedRoute>
                  <NewRequest />
                </ProtectedRoute>
              }
            />
            <Route
              path="/change/requests/:id/edit"
              element={
                <ProtectedRoute>
                  <EditRequest />
                </ProtectedRoute>
              }
            />
            <Route
              path="/change/users"
              element={
                <ProtectedRoute>
                  <Users />
                </ProtectedRoute>
              }
            />
          </Route>

          <Route element={<AutoLayout />}>
            <Route
              path="/autoline/dashboard"
              element={
                <ProtectedRoute>
                  <AutoDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/autoline/requests/new"
              element={
                <ProtectedRoute>
                  <NewAutoRequest />
                </ProtectedRoute>
              }
            />
            <Route
              path="/autoline/requests"
              element={
                <ProtectedRoute>
                  <AllAutoRequests />
                </ProtectedRoute>
              }
            />

            <Route
              path="/autoline/requests/:id"
              element={
                <ProtectedRoute>
                  <OneAutoRequest />
                </ProtectedRoute>
              }
            />
            <Route
              path="/autoline/companies"
              element={
                <ProtectedRoute>
                  <Companies />
                </ProtectedRoute>
              }
            />
            <Route
              path="/autoline/units"
              element={
                <ProtectedRoute>
                  <Units />
                </ProtectedRoute>
              }
            />
            <Route
              path="/autoline/modules"
              element={
                <ProtectedRoute>
                  <Modules />
                </ProtectedRoute>
              }
            />

            <Route
              path="/autoline/matrix"
              element={
                <ProtectedRoute>
                  <Roles />
                </ProtectedRoute>
              }
            />
            <Route
              path="/autoline/users"
              element={
                <ProtectedRoute>
                  <Users />
                </ProtectedRoute>
              }
            />
          </Route>
          <Route path="*" element={<Change />} />
        </Routes>
      </AuthProvider>
    </QueryClientProvider>
  );
}

function ProtectedRoute({ children }: { children: JSX.Element }) {
  let { token } = React.useContext(AuthContext);
  let location = useLocation();
  if (!token) {
    return <Navigate to="/" state={{ from: location }} replace />;
  }
  return children;
}

export default App;
